import React, { Fragment, useContext } from "react"
import { MobergButton, MobergButtonVariant, MobergColumn, MobergDropdown, MobergInputLabel, MobergTheme } from "../../../../../Moberg"
import { getSourceQEEGDSATraceName } from "../../../../../Pages/Data/Visualize/DataReview/Types/AnalysisDetails"
import {
	DEFAULT_SPECTROGRAM_TRACE_SETTINGS,
	RenderStrategy,
	SpectrogramTraceConfigJSON,
	TraceConfigJSON,
	TraceOption,
} from "../../../../../Pages/Data/Visualize/DataReview/Types/Trace"
import { ConfigureWindowModalContext } from "../ConfigureWindowModal"
import { SourceDataOriginSummary } from "../SourceDataOriginSummary"
import { SpectrogramGraphModalitiesContext, SpectrogramTraceSettingsCells, useReasonAddSpectrogramTraceIsDisabled } from "./SpectrogramTraceSettings"

// Pre-computed (offline) DSA modalities read straight from the archive. The trace
// carries no onDemandAnalysis: its dataKey is the archive modality itself, and the
// stored frame is rendered verbatim -- never recomputed (MCP-7288).
export const SourceSpectrogramModalities: React.FC = () => {
	const { sourceTraceOptions, updateGraphProperty } = useContext(ConfigureWindowModalContext)
	const { activeSourceSpectrogramTraces, updateTrace } = useContext(SpectrogramGraphModalitiesContext)

	// Options come from the resolved review sources, so a trace added here reads
	// from the associated CNS archive when an EDF is under review. Flag and
	// offline-file (",MCP") gating are already encoded in supportedRenderStrategies.
	const sourceDataTraceOptions = sourceTraceOptions.filter((option: TraceOption) =>
		!option.value.onDemandAnalysis && option.supportedRenderStrategies.includes(RenderStrategy.SPECTROGRAM)
	)
	const reasonAddTraceIsDisabled = useReasonAddSpectrogramTraceIsDisabled(sourceDataTraceOptions, "No pre-computed DSA source data available.")

	const handleAddTrace = () => {
		const defaultOption = sourceDataTraceOptions[0]

		if (!defaultOption) {
			return
		}

		const newTrace: SpectrogramTraceConfigJSON = {
			...defaultOption.value,
			...DEFAULT_SPECTROGRAM_TRACE_SETTINGS,
			id: `trace-${new Date(Date.now()).toISOString()}`,
			renderStrategy: RenderStrategy.SPECTROGRAM,
			isVisible: true,
		} as SpectrogramTraceConfigJSON

		updateGraphProperty("traces", (previous: TraceConfigJSON[] = []) => [...previous, newTrace])
		updateTrace(newTrace.id, newTrace, { useModalitySpecificDefaults: true })
		// After the defaults pass: applyModalitySpecificDefaults overwrites `name`
		// with the shared LABEL_SETTINGS entry ("qEEG DSA"), which loses the group.
		updateTrace(newTrace.id, { name: getSourceQEEGDSATraceName(defaultOption.value.rawDataKey) })
	}

	const handleModalityChange = (traceId: string, seed: TraceOption["value"]) => {
		updateTrace(traceId, { ...seed }, { useModalitySpecificDefaults: true })
		updateTrace(traceId, { name: getSourceQEEGDSATraceName(seed.rawDataKey) })
	}

	return (
		<MobergColumn gap="16px" style={{ padding: "0 16px" }}>
			<SourceDataOriginSummary />

			<div
				style={{
					display: "grid",
					gridTemplateColumns: "224px 180px 180px 180px 120px 120px 120px 80px 48px",
					columnGap: "16px",
					rowGap: "8px",
					alignItems: "center",
					maxWidth: "fit-content",
				}}
			>
				<MobergInputLabel text="Source" />
				<MobergInputLabel text="Label" />
				<MobergInputLabel text="Display scale" />
				<MobergInputLabel text="Color spectrum" />
				<MobergInputLabel text="Lower bound" />
				<MobergInputLabel text="" />
				<MobergInputLabel text="Upper bound" />
				<MobergInputLabel text="Visible" />
				<MobergInputLabel text="" />

				{activeSourceSpectrogramTraces.map(trace => (
					<Fragment key={trace.id}>
						<MobergDropdown
							dataTestId="spectrogram-source-select"
							options={sourceDataTraceOptions}
							// No fallback to another option: if the saved modality is not
							// currently offered, showing a different one would misrepresent
							// what the trace actually requests. The dropdown shows its
							// placeholder instead.
							selectedValue={sourceDataTraceOptions.find(option => option.value.rawDataKey === trace.rawDataKey)?.value}
							onChange={seed => handleModalityChange(trace.id, seed)}
							equals={(a, b) => a?.rawDataKey === b?.rawDataKey && a?.dataSource === b?.dataSource}
							width={224}
						/>

						<SpectrogramTraceSettingsCells trace={trace} testIdPrefix="spectrogram-source" labelInputWidth="170px" labelCharacterLimit={32} />
					</Fragment>
				))}

				<MobergButton
					dataTestId="spectrogram-add-source-button"
					onClick={handleAddTrace}
					theme={MobergTheme.BLUE}
					variant={MobergButtonVariant.OUTLINE}
					style={{ width: "224px" }}
					disabled={!!reasonAddTraceIsDisabled}
					tooltip={reasonAddTraceIsDisabled}
				>
					Add source data
				</MobergButton>
			</div>
		</MobergColumn>
	)
}
